import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import styled from 'styled-components'
import { Loading } from '../../atoms/Loading'
import { Flex } from '../../atoms/Flex'
import { PriceByPropertyTypeChartProps } from './PriceByPropertyTypeChart.types'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 350px;
  position: relative;
`

const ChartWrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height}px;
`

const ErrorContainer = styled(Flex)`
  height: 350px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const LoadingContainer = styled(Flex)`
  height: 350px;
`

export const PriceByPropertyTypeChart: React.FC<
  PriceByPropertyTypeChartProps
> = ({
  data,
  loading = false,
  error = null,
  title = 'Price by Property Type',
  height = 400,
  orientation = 'horizontal',
  formatCurrency = (value: number) => {
    if (value >= 1e9) return `CHF ${(value / 1e9).toFixed(1)}B`
    if (value >= 1e6) return `CHF ${(value / 1e6).toFixed(1)}M`
    if (value >= 1e3) return `CHF ${(value / 1e3).toFixed(0)}K`
    return `CHF ${value.toFixed(0)}`
  },
}) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useEffect(() => {
    if (!loading && !error && data && data.length > 0 && chartRef.current) {
      initializeChart()
    }

    return () => {
      chartInstance.current?.dispose()
    }
  }, [data, loading, error, orientation])

  useEffect(() => {
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const initializeChart = () => {
    if (!chartRef.current || !data) return

    if (chartInstance.current) {
      chartInstance.current.dispose()
    }

    chartInstance.current = echarts.init(chartRef.current)

    // Sort data by price descending
    const sortedData = [...data].sort((a, b) => b.price - a.price)

    const isHorizontal = orientation === 'horizontal'

    const option: echarts.EChartsOption = {
      title: {
        text: title,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          const param = params[0]
          const item = sortedData[param.dataIndex]
          return `
            <div style="padding: 8px;">
              <div style="font-weight: 600; margin-bottom: 4px;">${param.name}</div>
              <div>Average Price: ${formatCurrency(param.value)}</div>
              ${item.count ? `<div>Number of Properties: ${item.count.toLocaleString()}</div>` : ''}
            </div>
          `
        },
      },
      grid: {
        left: isHorizontal ? '15%' : '10%',
        right: '10%',
        bottom: isHorizontal ? '10%' : '15%',
        top: '15%',
        containLabel: true,
      },
      xAxis: isHorizontal
        ? {
            type: 'value',
            name: 'Average Price (CHF)',
            nameLocation: 'middle',
            nameGap: 30,
            axisLabel: {
              formatter: (value: number) => formatCurrency(value),
            },
          }
        : {
            type: 'category',
            data: sortedData.map((d) => d.type),
            axisLabel: {
              rotate: 45,
              fontSize: 11,
            },
          },
      yAxis: isHorizontal
        ? {
            type: 'category',
            data: sortedData.map((d) => d.type),
            axisLabel: {
              fontSize: 12,
            },
          }
        : {
            type: 'value',
            name: 'Average Price (CHF)',
            nameLocation: 'middle',
            nameGap: 50,
            axisLabel: {
              formatter: (value: number) => formatCurrency(value),
            },
          },
      series: [
        {
          type: 'bar',
          data: sortedData.map((d) => d.price),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(
              isHorizontal ? 1 : 0,
              0,
              isHorizontal ? 0 : 0,
              isHorizontal ? 0 : 1,
              [
                { offset: 0, color: '#10b981' },
                { offset: 1, color: '#047857' },
              ]
            ),
            borderRadius: isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
          },
          emphasis: {
            itemStyle: {
              color: new echarts.graphic.LinearGradient(
                isHorizontal ? 1 : 0,
                0,
                isHorizontal ? 0 : 0,
                isHorizontal ? 0 : 1,
                [
                  { offset: 0, color: '#34d399' },
                  { offset: 1, color: '#059669' },
                ]
              ),
            },
          },
          label: {
            show: true,
            position: isHorizontal ? 'right' : 'top',
            formatter: (params: any) => formatCurrency(params.value),
            fontSize: 11,
            color: '#374151',
          },
        },
      ],
    }

    chartInstance.current.setOption(option)
  }

  if (loading) {
    return (
      <LoadingContainer justify="center" align="center">
        <Loading size="large" />
      </LoadingContainer>
    )
  }

  if (error) {
    return (
      <ErrorContainer justify="center" align="center">
        {error}
      </ErrorContainer>
    )
  }

  if (!data || data.length === 0) {
    return (
      <ErrorContainer justify="center" align="center">
        No price by property type data available
      </ErrorContainer>
    )
  }

  return (
    <ChartContainer>
      <ChartWrapper ref={chartRef} $height={height} />
    </ChartContainer>
  )
}
